/**
 * Menu du compte sur ordinateur (propriétaire, 11, 12/10 et 24/10/2026), comme
 * celui de Claude : un clic sur son nom, en bas de la colonne, ouvre un menu
 * court en trois groupes séparés d'un filet —
 *   0. l'adresse du compte en tête, puis ses entreprises quand il en a
 *      plusieurs (25/10/2026) : l'actuelle cochée, un clic passe à une autre ;
 *   1. Paramètres (aussi par Ctrl+, / ⌘+,), Mon entreprise, le thème dans un
 *      sous-menu, l'aide ;
 *   2. les autres pages du compte (absences, position, astreintes, documents) ;
 *   3. l'abonnement, puis la déconnexion.
 * Paramètres et Mon entreprise s'ouvrent dans la fenêtre des paramètres, par-
 * dessus la page en cours (`app/settings-overlay.ts`). Sur téléphone, la photo
 * de la barre du bas ouvre toujours le Menu.
 *
 * Clavier : flèches haut/bas entre les entrées, flèche droite (ou Entrée) ouvre
 * le sous-menu du thème, flèche gauche ou Échap y revient ; Échap referme le
 * menu et rend le focus au bouton. Un clic à l'extérieur ou une navigation
 * referme aussi.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronDown, ChevronRight, Clock, Monitor, Moon, SunMoon, Sun, type LucideIcon } from 'lucide-react';
import { ICONS } from '../design/icons.js';
import { useAuth, useCan } from '../core/auth.js';
import { useSignOut } from './SignOutGuard.js';
import { roleDisplayName, t } from '../core/i18n.js';
import type { Theme } from '../core/theme.js';
import { PlanBadge } from './PlanBadge.js';
import { companyLine, useCompanySwitch } from './CompanySwitcher.js';
import { UserAvatar } from './UserAvatar.js';
import { useOtherPages } from '../app/use-other-pages.js';
import { SETTINGS_HOME, settingsLinkState } from '../app/settings-overlay.js';

/** Place du menu, calculée depuis le pied de colonne (variables de `.account-menu`). */
type MenuPosition = Partial<Record<'--menu-left' | '--menu-bottom' | '--menu-width', string>>;

/** Entrées du menu principal ; celles du sous-menu sont cherchées dans le sous-menu. */
const ROOT_ITEMS = ':scope > [role="menuitem"], :scope > [role="menuitemradio"], :scope > .account-menu__sub > [role="menuitem"]';
const SUB_ITEMS = '[role="menuitemradio"]';

/** Les quatre thèmes, dans le sous-menu : la place n'y manque plus (24/10/2026). */
const MENU_THEMES: readonly { mode: Theme; icon: LucideIcon }[] = [
  { mode: 'heure', icon: Clock },
  { mode: 'clair', icon: Sun },
  { mode: 'sombre', icon: Moon },
  { mode: 'auto', icon: Monitor },
];

/** Mac : ⌘, ; partout ailleurs Ctrl+, (affiché à droite de « Paramètres », comme Claude). */
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);

/** Pages rangées dans le premier groupe, avec Paramètres ; les autres forment le deuxième. */
const FIRST_GROUP = new Set(['entreprise', 'aide']);

export function AccountMenu(props: { collapsed: boolean }): ReactNode {
  const { me, theme, setTheme } = useAuth();
  const signOut = useSignOut();
  const can = useCan();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [position, setPosition] = useState<MenuPosition>({});
  const trigger = useRef<HTMLButtonElement>(null);
  const themeTrigger = useRef<HTMLButtonElement>(null);
  const submenu = useRef<HTMLDivElement>(null);
  /** Le sous-menu a été ouvert au clavier : son premier choix prend le focus. */
  const focusSubmenu = useRef(false);
  const otherPages = useOtherPages(me, location.pathname, open);
  const menu = useRef<HTMLDivElement>(null);
  // Envois en attente par entreprise : relus à chaque ouverture du menu.
  const companies = useCompanySwitch(open);

  // Toute navigation referme le menu : il ne survit pas à la page.
  useEffect(() => setOpen(false), [location.pathname, location.search]);
  // Une autre entreprise ouverte (déjà sur l'accueil, l'adresse ne change pas) : le menu se referme aussi.
  useEffect(() => setOpen(false), [me?.id]);
  useEffect(() => {
    if (!open) setThemeOpen(false);
  }, [open]);

  // Raccourci des grandes applications : Ctrl+, (⌘+, sur Mac) ouvre ses réglages.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== ',' || !(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) {
        return;
      }
      event.preventDefault();
      setOpen(false);
      navigate(SETTINGS_HOME, { state: settingsLinkState(location) });
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navigate, location]);

  // Au-dessus du pied de colonne et de sa largeur, ou à droite quand la colonne est réduite.
  useLayoutEffect(() => {
    if (!open || !trigger.current) return;
    const rect = trigger.current.getBoundingClientRect();
    const foot = trigger.current.closest('.side-nav-foot')?.getBoundingClientRect() ?? rect;
    setPosition(
      props.collapsed
        ? { '--menu-left': `${rect.right + 8}px`, '--menu-bottom': `${Math.max(8, window.innerHeight - rect.bottom)}px` }
        : {
            '--menu-left': `${foot.left}px`,
            '--menu-bottom': `${window.innerHeight - rect.top + 8}px`,
            '--menu-width': `${Math.max(foot.width, 248)}px`,
          },
    );
  }, [open, props.collapsed]);

  useEffect(() => {
    if (!themeOpen || !focusSubmenu.current) return;
    focusSubmenu.current = false;
    const items = [...(submenu.current?.querySelectorAll<HTMLElement>(SUB_ITEMS) ?? [])];
    (items.find((item) => item.getAttribute('aria-checked') === 'true') ?? items[0])?.focus();
  }, [themeOpen]);

  useEffect(() => {
    if (!open) return undefined;
    const rootItems = (): HTMLElement[] => [
      ...(menu.current?.querySelectorAll<HTMLElement>(ROOT_ITEMS) ?? []),
    ];
    rootItems()[0]?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      const inSubmenu = Boolean(submenu.current?.contains(document.activeElement));
      if (event.key === 'Escape' || (inSubmenu && event.key === 'ArrowLeft')) {
        event.preventDefault();
        if (inSubmenu) {
          setThemeOpen(false);
          themeTrigger.current?.focus();
          return;
        }
        setOpen(false);
        trigger.current?.focus();
        return;
      }
      if (event.key === 'ArrowRight' && document.activeElement === themeTrigger.current) {
        event.preventDefault();
        focusSubmenu.current = true;
        setThemeOpen(true);
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      const list = inSubmenu
        ? [...(submenu.current?.querySelectorAll<HTMLElement>(SUB_ITEMS) ?? [])]
        : rootItems();
      if (list.length === 0) return;
      event.preventDefault();
      if (!inSubmenu) setThemeOpen(false);
      const current = list.indexOf(document.activeElement as HTMLElement);
      const next =
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? list.length - 1
            : event.key === 'ArrowDown'
              ? (current + 1) % list.length
              : (current - 1 + list.length) % list.length;
      list[next]?.focus();
    };
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;
      if (!menu.current?.contains(target) && !trigger.current?.contains(target)) setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  if (!me) return null;
  const subscription = can('settings.manage');
  const account = me.email ?? me.username ?? '';
  const workplace = `${me.company_name} · ${roleDisplayName({ role: me.role, company_role_name: me.hierarchy?.roleName ?? null })}`;
  const overlay = settingsLinkState(location);
  const company = otherPages.find((page) => page.key === 'entreprise');
  const help = otherPages.find((page) => page.key === 'aide');
  const others = otherPages.filter((page) => !FIRST_GROUP.has(page.key));
  const ThemeIcon = MENU_THEMES.find((item) => item.mode === theme)?.icon ?? SunMoon;

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={`side-nav-user${open ? ' is-open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`${me.full_name} — ${workplace}`}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="avatar side-nav-user__avatar" aria-hidden="true">
          <UserAvatar
            fullName={me.full_name}
            avatarUrl={me.avatar_url}
            avatarFileId={me.avatar_file_id}
            bare
          />
        </span>
        {/* Une seule ligne, comme Claude : nom puis offre en discret. L'entreprise
            et le rôle sont au survol et en tête du menu. */}
        <span className="side-nav-user__identity">
          <span className="side-nav-user__name">{me.full_name}</span>
          <PlanBadge subtle />
        </span>
        {/* Le nom lu commence par le texte visible (WCAG 2.5.3), puis dit ce que fait le bouton. */}
        <span className="sr-only"> — {t('nav.menuCompte').toLowerCase()}</span>
        <ChevronDown className="side-nav-user__chevron" size={15} aria-hidden="true" />
      </button>
      {open ? (
        <div
          ref={menu}
          className="account-menu"
          role="menu"
          aria-label={t('nav.menuCompte')}
          // La fenêtre des paramètres garde la page en fond : son adresse ne change
          // pas, le choix d'une entrée referme donc le menu lui-même.
          onClick={(event) => {
            if ((event.target as HTMLElement).closest('a[role="menuitem"]')) setOpen(false);
          }}
          style={{ '--menu-left': position['--menu-left'], '--menu-bottom': position['--menu-bottom'], '--menu-width': position['--menu-width'] }}
        >
          {/* Comme Claude : le compte connecté en tête, en discret. */}
          <div className="account-menu__head" role="presentation">
            {account ? <span className="account-menu__account">{account}</span> : null}
            {/* Plusieurs entreprises : la liste qui suit dit l'actuelle et le rôle. */}
            {companies.companies.length ? null : <span className="account-menu__workplace">{workplace}</span>}
          </div>
          {companies.error ? <p className="account-menu__error" role="alert">{companies.error}</p> : null}
          {companies.companies.map((company) => {
            const waiting = companies.waiting[company.userId] ?? 0;
            const unavailable = company.status !== 'actif' && !company.advisorHold;
            return (
              <button
                key={company.userId}
                type="button"
                role="menuitemradio"
                aria-checked={company.current}
                aria-disabled={unavailable || undefined}
                className="account-menu__item account-menu__company"
                title={waiting ? `${t('compteEntreprises.envois', { n: waiting })} · ${t('compteEntreprises.envoisAide')}` : undefined}
                onClick={() => {
                  if (company.current || unavailable || companies.pending) return;
                  // Place de comptable en attente : sa fiche l'ouvrira (page du compte).
                  if (company.advisorHold) navigate('/compte/comptable');
                  else void companies.choose(company.userId);
                }}
              >
                <ICONS.entreprise size={18} aria-hidden="true" />
                <span className="account-menu__label">
                  {company.companyName}
                  <small className="account-menu__company-line">
                    {companies.pending === company.userId ? t('compteEntreprises.enCours') : companyLine(company)}
                  </small>
                </span>
                {waiting ? <span className="account-menu__value">{t('compteEntreprises.envois', { n: waiting })}</span> : null}
                {company.current ? <Check className="account-menu__check" size={16} aria-hidden="true" /> : null}
              </button>
            );
          })}
          {companies.companies.length ? <div className="account-menu__separator" role="separator" /> : null}
          <Link
            role="menuitem"
            className="account-menu__item"
            to={SETTINGS_HOME}
            state={overlay}
            aria-keyshortcuts={IS_MAC ? 'Meta+Comma' : 'Control+Comma'}
          >
            <ICONS.parametres size={18} aria-hidden="true" />
            <span className="account-menu__label">{t('nav.parametres')}</span>
            <kbd className="account-menu__shortcut" aria-hidden="true">{IS_MAC ? '⌘,' : 'Ctrl+,'}</kbd>
          </Link>
          {company ? (
            <Link role="menuitem" className="account-menu__item" to={company.to} state={overlay}>
              <company.icon size={18} aria-hidden="true" />
              <span className="account-menu__label">{company.label}</span>
            </Link>
          ) : null}
          <div
            className="account-menu__sub"
            onMouseEnter={() => setThemeOpen(true)}
            onMouseLeave={() => setThemeOpen(false)}
          >
            <button
              ref={themeTrigger}
              type="button"
              role="menuitem"
              className="account-menu__item"
              aria-haspopup="menu"
              aria-expanded={themeOpen}
              // Le survol l'a souvent déjà ouvert : un clic ne doit pas le refermer.
              onClick={() => setThemeOpen(true)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                focusSubmenu.current = true;
                setThemeOpen(true);
              }}
            >
              <ThemeIcon size={18} aria-hidden="true" />
              <span className="account-menu__label">{t('parametres.theme')}</span>
              <span className="account-menu__value">{t(`parametres.themes.${theme}`)}</span>
              <ChevronRight className="account-menu__chevron" size={16} aria-hidden="true" />
            </button>
            {themeOpen ? (
              <div ref={submenu} className="account-menu__submenu" role="menu" aria-label={t('parametres.theme')}>
                <div className="account-menu__panel">
                  {MENU_THEMES.map(({ mode, icon: Icon }) => (
                    <button
                      key={mode}
                      type="button"
                      role="menuitemradio"
                      aria-checked={theme === mode}
                      className="account-menu__item"
                      onClick={() => setTheme(mode)}
                    >
                      <Icon size={18} aria-hidden="true" />
                      <span className="account-menu__label">{t(`parametres.themes.${mode}`)}</span>
                      {theme === mode ? <Check className="account-menu__check" size={16} aria-hidden="true" /> : null}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          {help ? (
            <Link role="menuitem" className="account-menu__item" to={help.to}>
              <help.icon size={18} aria-hidden="true" />
              <span className="account-menu__label">{help.label}</span>
            </Link>
          ) : null}
          {others.length > 0 ? <div className="account-menu__separator" role="separator" /> : null}
          {/* Les mêmes pages que le Menu du téléphone : une seule liste (app/use-other-pages.ts). */}
          {others.map((page) => (
            <Link key={page.key} role="menuitem" className="account-menu__item" to={page.to}>
              <page.icon size={18} aria-hidden="true" />
              <span className="account-menu__label">{page.label}</span>
            </Link>
          ))}
          <div className="account-menu__separator" role="separator" />
          {subscription ? (
            <Link role="menuitem" className="account-menu__item" to="/entreprise/abonnement" state={overlay}>
              <ICONS.abonnement size={18} aria-hidden="true" />
              <span className="account-menu__label">{t('nav.abonnementFacturation')}</span>
            </Link>
          ) : null}
          <button
            role="menuitem"
            type="button"
            className="account-menu__item account-menu__logout"
            onClick={() => {
              setOpen(false);
              signOut();
            }}
          >
            <ICONS.deconnexion size={18} aria-hidden="true" />
            <span className="account-menu__label">{t('auth.deconnexion')}</span>
          </button>
        </div>
      ) : null}
    </>
  );
}
